// Comment Card Component
// Individual comment card for post detail view

import React, { useState } from 'react';
import { Comment } from '../../types/community';
import { useChatRoomTheme } from '../../contexts/ChatRoomThemeContext';
import { RichBody } from './RichBody';
import { EmojiPicker } from './EmojiPicker';
import {
  CommentCardContainer,
  CommentVoteSection,
  CommentVoteButton,
  CommentVoteCount,
  CommentContent,
  CommentMeta,
  CommentAuthor,
  CommentTime,
  CommentBody,
  CommentActions,
  ReplyButton,
  ReplyBox,
  ReplyTextarea,
  ReplyBoxActions,
  ReplyCancelButton,
  ReplySubmitButton,
  RepliesList,
} from '../../styles/CommunityChatRooms/CommentCard.styles';

interface CommentCardProps {
  comment: Comment;
  replies?: Comment[];
  onVote: (commentId: string, voteType: 'upvote' | 'downvote') => void;
  onReply?: (parentCommentId: string, body: string) => Promise<void>;
  /** Open the author's profile over the room. Passed down to replies too. */
  onAuthorClick?: (authorId: string, authorName: string) => void;
}

const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return `${Math.floor(diffInSeconds / 604800)}w ago`;
};

export const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  replies = [],
  onVote,
  onReply,
  onAuthorClick,
}) => {
  const { theme } = useChatRoomTheme();
  const score = comment.upvotes - comment.downvotes;
  const isUpvoted = comment.userVote === 'upvote';
  const isDownvoted = comment.userVote === 'downvote';

  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const handleUpvote = () => {
    onVote(comment.id, 'upvote');
  };

  const handleDownvote = () => {
    onVote(comment.id, 'downvote');
  };

  const handleSubmitReply = async () => {
    if (!replyText.trim() || !onReply) return;
    setIsSubmittingReply(true);
    try {
      await onReply(comment.id, replyText.trim());
      setReplyText('');
      setShowReplyBox(false);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  return (
    <CommentCardContainer $theme={theme}>
      <CommentVoteSection>
        <CommentVoteButton
          $isActive={isUpvoted}
          $direction="up"
          $theme={theme}
          onClick={handleUpvote}
          aria-label="Upvote"
        >
          ▲
        </CommentVoteButton>
        <CommentVoteCount $isPositive={score > 0} $theme={theme}>
          {score > 0 ? `+${score}` : score.toString()}
        </CommentVoteCount>
        <CommentVoteButton
          $isActive={isDownvoted}
          $direction="down"
          $theme={theme}
          onClick={handleDownvote}
          aria-label="Downvote"
        >
          ▼
        </CommentVoteButton>
      </CommentVoteSection>

      <CommentContent>
        <CommentMeta $theme={theme}>
          <CommentAuthor
            $theme={theme}
            $clickable={!!onAuthorClick && !!comment.authorId}
            onClick={onAuthorClick && comment.authorId
              ? () => onAuthorClick(comment.authorId, comment.authorName)
              : undefined}
            role={onAuthorClick && comment.authorId ? 'button' : undefined}
            tabIndex={onAuthorClick && comment.authorId ? 0 : undefined}
            onKeyDown={onAuthorClick && comment.authorId
              ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onAuthorClick(comment.authorId, comment.authorName);
                }
              }
              : undefined}
            aria-label={onAuthorClick && comment.authorId ? `View ${comment.authorName}'s profile` : undefined}
          >
            {comment.authorName}
          </CommentAuthor>
          <CommentTime>{formatTimeAgo(comment.createdAt)}</CommentTime>
        </CommentMeta>
        <CommentBody $theme={theme}><RichBody text={comment.body} /></CommentBody>
        {onReply && (
          <CommentActions>
            <ReplyButton $theme={theme} onClick={() => setShowReplyBox((prev) => !prev)}>
              {showReplyBox ? 'Cancel' : 'Reply'}
            </ReplyButton>
          </CommentActions>
        )}

        {showReplyBox && (
          <ReplyBox>
            <ReplyTextarea
              $theme={theme}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={`Replying to ${comment.authorName}...`}
              autoFocus
            />
            <ReplyBoxActions>
              <EmojiPicker
                theme={theme}
                onSelect={(emoji) => setReplyText((prev) => prev + emoji)}
                label="Add an emoji to your reply"
              />
              <ReplyCancelButton $theme={theme} onClick={() => { setShowReplyBox(false); setReplyText(''); }}>
                Cancel
              </ReplyCancelButton>
              <ReplySubmitButton onClick={handleSubmitReply} disabled={!replyText.trim() || isSubmittingReply}>
                {isSubmittingReply ? 'Replying...' : 'Reply'}
              </ReplySubmitButton>
            </ReplyBoxActions>
          </ReplyBox>
        )}

        {replies.length > 0 && (
          <RepliesList $theme={theme}>
            {replies.map((reply) => (
              <CommentCard
                key={reply.id}
                comment={reply}
                onVote={onVote}
                onAuthorClick={onAuthorClick}
              />
            ))}
          </RepliesList>
        )}
      </CommentContent>
    </CommentCardContainer>
  );
};
